import { useState } from "react";
import { Select, Tag } from "antd";
import { useQuery } from "shared/hooks/useApi";
import {
  FIELD_REGISTRY,
  ACTION_CATALOG,
} from "utils/api/graphql/queries/screens";

const SlotInspector = ({ slot, onSlotUpdate, onClose, processId }) => {
  if (!slot) {
    return (
      <div className="screen-studio__inspector-empty">
        <p>Sélectionnez un slot dans l'aperçu pour le configurer.</p>
      </div>
    );
  }

  return (
    <div className="screen-studio__inspector-content">
      <div className="screen-studio__inspector-header">
        <h4>{slot.id}</h4>
        <Tag>{slot.type}</Tag>
        <button className="screen-studio__close" onClick={onClose}>
          ✕
        </button>
      </div>

      {slot.type === "text" && (
        <TextSlotEditor
          slot={slot}
          onUpdate={onSlotUpdate}
          processId={processId}
        />
      )}
      {slot.type === "input" && (
        <InputSlotEditor
          slot={slot}
          onUpdate={onSlotUpdate}
          processId={processId}
        />
      )}
      {slot.type === "image" && (
        <ImageSlotEditor
          slot={slot}
          onUpdate={onSlotUpdate}
          processId={processId}
        />
      )}
      {slot.type === "list" && (
        <ListSlotEditor
          slot={slot}
          onUpdate={onSlotUpdate}
          processId={processId}
        />
      )}
      {slot.type === "action" && (
        <ActionSlotEditor slot={slot} onUpdate={onSlotUpdate} />
      )}

      {slot.localizableAttributes && slot.localizableAttributes.length > 0 && (
        <div className="screen-studio__inspector-section">
          <h5>Attributs localisables</h5>
          {slot.localizableAttributes.map((attr) => (
            <div key={attr.i18nKey} className="screen-studio__attr-row">
              <Tag>{attr.attr}</Tag>
              <span>{attr.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TextSlotEditor = ({ slot, onUpdate, processId }) => {
  const [bindingKind, setBindingKind] = useState(
    slot.binding?.kind || "static_i18n",
  );

  return (
    <div className="screen-studio__inspector-section">
      <h5>Binding</h5>
      <Select
        value={bindingKind}
        onChange={(value) => {
          setBindingKind(value);
          if (value === "static_i18n") {
            onUpdate(slot.id, {
              kind: "static_i18n",
              i18nKey: slot.id,
              sourceValue: slot.binding?.sourceValue || "",
            });
          }
        }}
        className="w-100"
      >
        <Select.Option value="static_i18n">Texte statique (i18n)</Select.Option>
        <Select.Option value="data">Lier à une donnée</Select.Option>
      </Select>

      {bindingKind === "static_i18n" && (
        <div className="m-top-8">
          <small>Valeur source : {slot.binding?.sourceValue}</small>
        </div>
      )}

      {bindingKind === "data" && (
        <DataSourcePicker
          value={slot.binding?.source}
          processId={processId}
          onChange={(source) =>
            onUpdate(slot.id, { kind: "data", source, fallback: "" })
          }
        />
      )}
    </div>
  );
};

const InputSlotEditor = ({ slot, onUpdate, processId }) => (
  <div className="screen-studio__inspector-section">
    <h5>Champ de saisie ({slot.inputKind})</h5>
    <DataSourcePicker
      value={
        slot.binding?.kind === "entity_field"
          ? `${slot.binding.entity}.${slot.binding.field}`
          : ""
      }
      writableOnly
      processId={processId}
      onChange={(source) => {
        const [entity, ...fieldParts] = source.split(".");
        onUpdate(slot.id, {
          kind: "entity_field",
          entity,
          field: fieldParts.join("."),
          save: "on_change_debounced:500ms",
        });
      }}
    />
  </div>
);

const ImageSlotEditor = ({ slot, onUpdate, processId }) => (
  <div className="screen-studio__inspector-section">
    <h5>Image</h5>
    <DataSourcePicker
      value={slot.binding?.source}
      processId={processId}
      onChange={(source) => onUpdate(slot.id, { kind: "data", source })}
    />
  </div>
);

const ActionSlotEditor = ({ slot, onUpdate }) => {
  const { data } = useQuery(ACTION_CATALOG);
  const actions = data?.actionCatalog || [];

  return (
    <div className="screen-studio__inspector-section">
      <h5>Action ({slot.binding?.sourceValue || "bouton"})</h5>
      <Select
        showSearch
        virtual={false}
        listHeight={300}
        value={
          slot.binding?.kind === "catalog_action"
            ? slot.binding.actionId
            : undefined
        }
        placeholder="Associer une action"
        onChange={(actionId) => {
          const action = actions.find((a) => a.id === actionId);
          onUpdate(slot.id, {
            kind: "catalog_action",
            actionId,
            description: action?.description,
          });
        }}
        className="w-100"
        filterOption={(input, option) =>
          (option.children || "")
            .toString()
            .toLowerCase()
            .indexOf(input.toLowerCase()) >= 0
        }
      >
        {actions.map((action) => (
          <Select.Option key={action.id} value={action.id}>
            {action.id} — {action.description?.slice(0, 60)}
          </Select.Option>
        ))}
      </Select>
      <div className="m-top-8">
        <Select
          value={
            slot.binding?.kind === "process_trigger"
              ? slot.binding.name
              : undefined
          }
          placeholder="Ou déclencher un trigger process"
          onChange={(name) =>
            onUpdate(slot.id, { kind: "process_trigger", name })
          }
          allowClear
          className="w-100"
        >
          <Select.Option value="next">Suivant</Select.Option>
          <Select.Option value="back">Retour</Select.Option>
          <Select.Option value="subscribeToPlan">Souscrire</Select.Option>
          <Select.Option value="sendQuote">Envoyer le devis</Select.Option>
          <Select.Option value="createContract">Créer le contrat</Select.Option>
          <Select.Option value="signDocuments">Signer</Select.Option>
          <Select.Option value="prepareComparison">
            Préparer le comparatif
          </Select.Option>
          <Select.Option value="downloadDocuments">
            Télécharger les documents
          </Select.Option>
          <Select.Option value="sendDocuments">
            Envoyer les documents
          </Select.Option>
        </Select>
      </div>
    </div>
  );
};

const ListSlotEditor = ({ slot, onUpdate, processId }) => (
  <div className="screen-studio__inspector-section">
    <h5>Liste</h5>
    <DataSourcePicker
      value={slot.binding?.source}
      collectionsOnly
      processId={processId}
      onChange={(source) =>
        onUpdate(slot.id, { kind: "data", source, itemBindings: [] })
      }
    />
  </div>
);

const DataSourcePicker = ({
  value,
  onChange,
  writableOnly,
  collectionsOnly,
  processId,
}) => {
  const { data } = useQuery(FIELD_REGISTRY, {
    variables: processId ? { process: { id: processId } } : {},
  });
  const registry = data?.fieldRegistry || [];

  const options = [];
  for (const entity of registry) {
    if (collectionsOnly && !entity.collection) continue;
    for (const field of entity.fields) {
      if (writableOnly && field.mode !== "WRITE") continue;
      const path = `${entity.key}.${field.key}`;
      options.push({
        value: path,
        label: field.label ? `${path} — ${field.label}` : path,
      });
    }
  }

  return (
    <div className="m-top-8">
      <Select
        showSearch
        virtual={false}
        listHeight={400}
        value={value || undefined}
        placeholder="Sélectionner un champ"
        onChange={onChange}
        className="w-100"
        filterOption={(input, option) =>
          (option.children || "")
            .toString()
            .toLowerCase()
            .indexOf(input.toLowerCase()) >= 0
        }
      >
        {options.map((opt) => (
          <Select.Option key={opt.value} value={opt.value}>
            {opt.label}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};

export default SlotInspector;
